import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useService } from "@/core/hooks/useService";
import { authStorageService } from "@/modules/auth/services/authStorageService";
import { hydratePlatformUser } from "@/modules/auth/services/hydratePlatformUser";
import {
  loadPlatformSessionOnce,
  resetPlatformSessionHydration,
} from "@/modules/auth/services/platformSessionHydration";
import { cognitoAuthService } from "@/modules/auth/services/cognitoAuthService";
import type { User } from "@/modules/auth/services/authService";
import { RouteLoadingFallback } from "@/shared/components/RouteLoadingFallback";
import { getErrorMessage } from "@/shared/utils/apiErrorMessage";

const CURRENT_USER_QUERY_KEY = ["auth", "currentUser"] as const;

interface AuthSessionBootstrapProps {
  children: ReactNode;
}

/**
 * On session restore: resolve tenant (bearer required), then refresh user context
 * from GET /platform/user-context so platform roles are available before the dashboard loads.
 */
export function AuthSessionBootstrap({ children }: AuthSessionBootstrapProps) {
  const authService = useService("auth");
  const queryClient = useQueryClient();
  const authServiceRef = useRef(authService);
  const queryClientRef = useRef(queryClient);
  authServiceRef.current = authService;
  queryClientRef.current = queryClient;
  const [ready, setReady] = useState(
    () => !authStorageService.isAuthenticated(),
  );
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!authStorageService.isAuthenticated()) {
        if (!cancelled) {
          setBootstrapError(null);
          setReady(true);
        }
        return;
      }

      if (!cancelled) setBootstrapError(null);

      try {
        await loadPlatformSessionOnce(async () => {
          const auth = authServiceRef.current;
          const cache = queryClientRef.current;
          if (!authStorageService.getCachedResolvedTenant()) {
            const resolved = await auth.resolveTenant();
            authStorageService.persistResolvedTenant(resolved);
            cache.setQueryData(["auth", "tenant"], resolved);
          }

          const current = authStorageService.getCurrentUser();
          if (current) {
            const hydrated = await hydratePlatformUser(auth, current);
            authStorageService.setUser(hydrated);
            cache.setQueryData<User>(CURRENT_USER_QUERY_KEY, hydrated);
          }
        });
        if (!cancelled) setReady(true);
      } catch (error) {
        console.error("Failed to bootstrap auth session:", error);
        if (!cancelled) {
          setBootstrapError(
            getErrorMessage(
              error,
              "Unable to load your account. Please sign in again.",
            ),
          );
          setReady(true);
        }
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSignOut = async () => {
    resetPlatformSessionHydration();
    authStorageService.markForceLogout();
    await cognitoAuthService.signOut();
    authStorageService.clear();
    queryClient.clear();
    window.location.href = "/auth";
  };

  if (!ready) {
    return <RouteLoadingFallback />;
  }

  if (bootstrapError && authStorageService.isAuthenticated()) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-lg border border-red-500/40 bg-slate-900/80 p-6 text-center">
          <p className="text-red-300 text-sm mb-4">{bootstrapError}</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => void handleSignOut()}
              className="px-4 py-2 rounded-lg border border-slate-600 text-gray-300 hover:bg-slate-800 text-sm font-medium transition"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
